import { FloatingPanelOptions } from '@shared/floating-panel/floating-panel.types';
import { HdGateInfoRequestedContext } from '../chart/bodygraph/gate-info/hd-gate-info-requested-context.model';
import { HdGateInfoRequestedComponent } from '../chart/bodygraph/gate-info/variants/hd-gate-info-requested.component';
import { HdBodygraphPlanetaryActivation } from '../chart/bodygraph/hd-bodygraph.models';
import { HdPlanetKey, getHdPlanetDisplayConfig } from '../chart/bodygraph/planet-grid/hd-planet-display-config';
import { getTransitSourceKey } from './transit.models';

/**
 * Doorklik van een transit (shift-popup, verticale dag-flow) naar de volledige
 * gate-info: de transiterende planeet gaat mee als chart-context, zodat de
 * gate-info haar lijn en fixaties toont zoals bij de Just Now-transit op de
 * chartpagina.
 */
export interface HdTransitGateInfoSource {
  readonly planetId: number;
  /** null als de planeet niet in de Maia-mapping zit — naam/glyph vallen dan weg. */
  readonly planetKey: HdPlanetKey | null;
  readonly gate: number;
  /** Lijn 1-6; `null` bij een zuiver poort-segment. */
  readonly line: number | null;
  /** sourceKey uit de API-context; anders afgeleid uit de planetKey. */
  readonly sourceKey?: string | null;
}

/**
 * Transit-activatie voor de gate-info (`side: 'personality'`, gate+line+planetId).
 * Kleur/toon/base/longitude zijn PHS-/zodiac-details die een transit-segment
 * niet kent; de gate-info gebruikt ze niet voor de weergave. Bij een zuiver
 * poort-segment (lijn onbekend) geldt de bestaande `line ?? 0`-sentinel.
 */
export function buildHdTransitGateInfoActivation(source: HdTransitGateInfoSource): HdBodygraphPlanetaryActivation {
  const config = source.planetKey != null ? getHdPlanetDisplayConfig(source.planetKey) : null;
  return {
    id: `transit-popup:${source.planetId}:${source.gate}:${source.line ?? 0}`,
    gate: source.gate,
    side: 'personality',
    sourceType: 'planet',
    sourceKey: source.sourceKey
      ?? (source.planetKey != null ? getTransitSourceKey(source.planetKey) : `planet-${source.planetId}`),
    planet: config?.label ?? `Planet ${source.planetId}`,
    planetId: source.planetId,
    line: source.line ?? 0,
    color: 0,
    tone: 0,
    base: 0,
    longitude: 0,
  };
}

/**
 * Floating-panel-opties van de volledige gate-info voor `gate` (dedup-key
 * `gate-info:<gate>`, dezelfde als de chart-editor: een al open poort wordt
 * gefocust i.p.v. gedupliceerd).
 *
 * De planeet staat enkel in háár poort: bij een andere poort (vorige/volgende)
 * blijven de `activations` leeg — anders zou de gate-info de planeet-invloed
 * van de verkeerde poort.lijn tonen. `transitActivations` blijft wél gevuld:
 * dat is de transit-snapshot als geheel, waarmee de gate-info fixaties via de
 * harmonische poort ziet.
 */
export function buildHdTransitGateInfoPanel(
  gate: number,
  source: HdTransitGateInfoSource,
): FloatingPanelOptions<HdGateInfoRequestedComponent, HdGateInfoRequestedContext> {
  const activation = buildHdTransitGateInfoActivation(source);
  return {
    key: `gate-info:${gate}`,
    component: HdGateInfoRequestedComponent,
    data: {
      kind: 'requested',
      gate,
      source: 'chart-gate',
      activations: gate === source.gate ? [activation] : [],
      transitActivations: [activation],
      isDummyActiveViaEditor: false,
      initialMode: 'full',
    },
    dockTab: { label: `${gate}` },
    dockZone: 'full',
  };
}
